import { useDispatch, useSelector } from 'react-redux';
import { clearItemFromCart, incrementItemInCart, removeItemFromCart, setCartItems } from '../../store/cart/cart.action';
import { selectCartItems } from '../../store/cart/cart.selector';
import './checkout-item.styles.jsx';
import { CheckoutItemContainer, HeaderText, PriceText, QuantityContainer, QuantityConfirmation, QuantityInfo, VariantText, CheckoutItemAction, HeaderItem, BundleChildrenContainer, BundleChildItem, CartAvailabilityError } from './checkout-item.styles.jsx';
import { useLocation, useParams } from 'react-router-dom';
import Quantity from '../shared/quantity/quantity.component';
import { useMemo, useState } from 'react';
import ButtonAction from '../shared/buttonAction/buttonAction.component';
import { getColor } from '../../utils/utils.common';
import { selectOnlineOrdersConfigs } from '../../store/kitchen/kitchen.selector';
import { fieldError } from '../../constants';

const EMPTY_MESSAGES = new Map();

const CheckoutItem = ({ cartItem, bundleChildren = [], bundleAggregate = null, availabilityMessages }) => {
    const { pathname } = useLocation();
    const { dishName, dishPriceWithVariants, quantity } = cartItem;
    const isBundleAggregate = Array.isArray(bundleAggregate?.parentIds) && bundleAggregate.parentIds.length > 0;
    const isBundleParent = (Array.isArray(bundleChildren) && bundleChildren.length > 0) || isBundleAggregate;
    const isBundleSubItem = Boolean(cartItem?.bundleParentId);
    const displayName = dishName;

    const onlineOrdersConfig = useSelector(selectOnlineOrdersConfigs);
    const cartItems = useSelector(selectCartItems);
    const dispatch = useDispatch();

    const createCartItemId = () =>
      `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const removeBundleInstancesByParentIds = (parentIds) => {
      const parentIdSet = new Set(parentIds);
      const newCartItems = cartItems.filter(
        (item) =>
          !parentIdSet.has(item.cartItemId) &&
          !parentIdSet.has(item.bundleParentId)
      );
      dispatch(setCartItems(newCartItems));
    };

    const clearItemHandler = () => {
        if (isBundleParent) {
            if (isBundleAggregate) {
              removeBundleInstancesByParentIds(bundleAggregate.parentIds);
              return;
            }
            removeBundleInstancesByParentIds([cartItem.cartItemId]);
            return;
        }
        dispatch(clearItemFromCart(cartItems, cartItem));
    };
    const addItemHandler = () => {
      if (isBundleAggregate) {
        const sourceParentId = bundleAggregate.parentIds[0];
        const sourceParent = cartItems.find(
          (item) => item.cartItemId === sourceParentId
        );
        if (!sourceParent) return;

        const sourceChildren = cartItems.filter(
          (item) => item.bundleParentId === sourceParentId
        );
        const nextParentId = createCartItemId();
        const nextBundleSignature = sourceParent?.bundleTemplateId
          ? `${sourceParent.bundleTemplateId}-${createCartItemId()}`
          : createCartItemId();

        const newParentItem = {
          ...sourceParent,
          cartItemId: nextParentId,
          quantity: 1,
          bundleSignature: nextBundleSignature,
        };

        const newChildItems = sourceChildren.map((child) => ({
          ...child,
          cartItemId: createCartItemId(),
          bundleParentId: nextParentId,
          quantity: 1,
          bundleSignature: nextBundleSignature,
        }));

        dispatch(setCartItems([...cartItems, newParentItem, ...newChildItems]));
        return;
      }

      dispatch(incrementItemInCart(cartItems, cartItem));
    };
    const removeItemHandler = () => {
      if (isBundleAggregate) {
        if ((bundleAggregate?.instanceCount || 1) <= 1) return;
        const parentIdToRemove = bundleAggregate.parentIds[bundleAggregate.parentIds.length - 1];
        if (!parentIdToRemove) return;
        removeBundleInstancesByParentIds([parentIdToRemove]);
        return;
      }

      dispatch(removeItemFromCart(cartItems, cartItem));
    };

    const [showQuantity, setShowQuantity] = useState(false)

    const { kitchenSlug } = useParams();

    const backgroundTertiary = getColor("background-tertiary", onlineOrdersConfig);
    const brandTertiary = getColor("brand-tertiary", onlineOrdersConfig);
    const formatSignedCharge = (charge) => {
      const value = Number(charge);
      if (!Number.isFinite(value)) return null;
      const sign = value >= 0 ? '+' : '-';
      return `${sign}$${Math.abs(value).toFixed(2)}`;
    };
    const isBasePriceVariantSelection = (variant, item = cartItem) => {
      if (variant?.isBasePriceModifier) return true;
      const group = (item?.variants || []).find(
        (v) => v?.variantName === variant?.optionCategory
      );
      return !!group?.isBasePriceModifier;
    };
    const formatVariantChargeSuffix = (variant, item = cartItem) => {
      if (!variant?.extraCharge || isBasePriceVariantSelection(variant, item)) return '';
      const formatted = formatSignedCharge(variant.charge);
      return formatted ? ` (${formatted})` : '';
    };
    const normalizeVariants = (rawVariants, item = cartItem) => {
      const list = Array.isArray(rawVariants)
        ? rawVariants.filter(Boolean)
        : rawVariants && typeof rawVariants === 'object'
          ? Object.values(rawVariants).filter(Boolean)
          : [];

      // Keep base-price selection first under the item name.
      return [...list].sort((a, b) => {
        const aBase = isBasePriceVariantSelection(a, item) ? 0 : 1;
        const bBase = isBasePriceVariantSelection(b, item) ? 0 : 1;
        return aBase - bBase;
      });
    };
    const getVariantLabel = (variant) => {
      if (typeof variant === 'string' || typeof variant === 'number' || typeof variant === 'boolean') {
        return String(variant).trim();
      }
      if (!variant || typeof variant !== 'object') return "";

      const prioritizedLabel = String(
        variant?.name ||
        variant?.option ||
        variant?.optionName ||
        variant?.label ||
        variant?.value ||
        variant?.optionCategory ||
        ""
      ).trim();
      if (prioritizedLabel) return prioritizedLabel;

      // Last-resort fallback for inconsistent payloads: pick first readable string value.
      const firstStringValue = Object.values(variant).find(
        (value) => typeof value === 'string' && value.trim().length > 0
      );
      return firstStringValue ? String(firstStringValue).trim() : "";
    };
    const itemQuantity = isBundleAggregate ? bundleAggregate.instanceCount : quantity;
    const itemTotalPrice = isBundleAggregate
      ? Number(bundleAggregate?.totalPrice ?? bundleAggregate.parentIds.reduce((total, parentId) => {
          const parentItem = cartItems.find((item) => item.cartItemId === parentId);
          if (!parentItem) return total;
          return total + (Number(parentItem.dishPriceWithVariants) * Number(parentItem.quantity || 1));
        }, 0))
      : Number(dishPriceWithVariants) * Number(quantity);

    const messages = availabilityMessages ?? EMPTY_MESSAGES;

    const bundleParentMessage = useMemo(() => {
      if (!isBundleParent) return null;
      if (isBundleAggregate) {
        for (const pid of bundleAggregate.parentIds) {
          const m = messages.get(pid);
          if (m) return m;
        }
        return null;
      }
      return messages.get(cartItem.cartItemId) ?? null;
    }, [
      isBundleParent,
      isBundleAggregate,
      bundleAggregate,
      cartItem.cartItemId,
      messages,
    ]);

    const nonBundleParentMessage =
      !isBundleParent ? (messages.get(cartItem.cartItemId) ?? null) : null;

    const availabilityErrorColor = fieldError;

    return (
        <CheckoutItemContainer backgroundTertiary={backgroundTertiary}>
            {(pathname === `/${kitchenSlug}/checkout/success` || pathname === `/${kitchenSlug}/checkout/payment`) ? (
                <>
                    <HeaderItem>
                        <HeaderText>
                            {displayName}
                            <QuantityConfirmation backgroundTertiary={backgroundTertiary}>
                                x{itemQuantity}
                            </QuantityConfirmation>
                        </HeaderText>
                        {!isBundleSubItem && (
                          <PriceText>${itemTotalPrice.toFixed(2)}</PriceText>
                        )}
                    </HeaderItem>
                    {normalizeVariants(cartItem.variantSelected).length > 0 && (
                        normalizeVariants(cartItem.variantSelected).map((variant, index) => (
                            <div key={`${getVariantLabel(variant)}-${index}`}>
                                <VariantText>{getVariantLabel(variant)}{formatVariantChargeSuffix(variant)}</VariantText>
                            </div>
                        ))
                    )}
                    {cartItem.note && <VariantText>Note: {cartItem.note}</VariantText>}
                    {isBundleParent && (
                      <BundleChildrenContainer
                        $hasParentVariants={
                          normalizeVariants(cartItem.variantSelected).length > 0
                        }
                      >
                        {bundleChildren.map((subItem) => (
                          <BundleChildItem key={subItem.cartItemId}>
                            <VariantText style={{ fontWeight: 600 }}>
                              {subItem.dishName}
                              {subItem.priceAdjustment ? ` (${Number(subItem.priceAdjustment) >= 0 ? '+' : ''}$${Math.abs(Number(subItem.priceAdjustment)).toFixed(2)})` : ''}
                            </VariantText>
                            {normalizeVariants(subItem.variantSelected, subItem).length > 0 && (
                              <VariantText style={{ opacity: 0.9 }}>
                                {normalizeVariants(subItem.variantSelected, subItem)
                                  .map(variant => `${getVariantLabel(variant)}${formatVariantChargeSuffix(variant, subItem)}`)
                                  .join(', ')}
                              </VariantText>
                            )}
                            {subItem.note && <VariantText>Note: {subItem.note}</VariantText>}
                          </BundleChildItem>
                        ))}
                      </BundleChildrenContainer>
                    )}
                </>
            ) : (
                <>
                    <HeaderItem>
                        <HeaderText>{displayName}</HeaderText>
                        {!isBundleSubItem && (
                          <PriceText>${itemTotalPrice.toFixed(2)}</PriceText>
                        )}
                    </HeaderItem>
                    {normalizeVariants(cartItem.variantSelected).length > 0 && (
                        normalizeVariants(cartItem.variantSelected).map((variant, index) => (
                            <div key={`${getVariantLabel(variant)}-${index}`}>
                                {/* <VariantText>{variant.optionCategory}: </VariantText> */}
                                <VariantText>{getVariantLabel(variant)}{formatVariantChargeSuffix(variant)}</VariantText>
                            </div>
                        ))
                    )}
                    {cartItem.note && <VariantText>Note: {cartItem.note}</VariantText>}
                    {isBundleParent && (
                      <BundleChildrenContainer
                        $hasParentVariants={
                          normalizeVariants(cartItem.variantSelected).length > 0
                        }
                      >
                        {bundleChildren.map((subItem) => {
                          const childMsg = messages.get(subItem.cartItemId);
                          return (
                          <BundleChildItem key={subItem.cartItemId}>
                            <VariantText style={{ fontWeight: 600 }}>
                              {subItem.dishName}
                              {subItem.priceAdjustment ? ` (${Number(subItem.priceAdjustment) >= 0 ? '+' : ''}$${Math.abs(Number(subItem.priceAdjustment)).toFixed(2)})` : ''}
                            </VariantText>
                            {normalizeVariants(subItem.variantSelected, subItem).length > 0 && (
                              <VariantText style={{ opacity: 0.9 }}>
                                {normalizeVariants(subItem.variantSelected, subItem)
                                  .map(variant => `${getVariantLabel(variant)}${formatVariantChargeSuffix(variant, subItem)}`)
                                  .join(', ')}
                              </VariantText>
                            )}
                            {subItem.note && <VariantText>Note: {subItem.note}</VariantText>}
                            {pathname === `/${kitchenSlug}/checkout` && childMsg && (
                              <CartAvailabilityError $color={availabilityErrorColor}>
                                {childMsg}
                              </CartAvailabilityError>
                            )}
                          </BundleChildItem>
                          );
                        })}
                      </BundleChildrenContainer>
                    )}
                    {pathname === `/${kitchenSlug}/checkout` &&
                      isBundleParent &&
                      bundleParentMessage && (
                        <CartAvailabilityError $color={availabilityErrorColor}>
                          {bundleParentMessage}
                        </CartAvailabilityError>
                      )}
                    {pathname === `/${kitchenSlug}/checkout` &&
                      nonBundleParentMessage && (
                      <CartAvailabilityError $color={availabilityErrorColor}>
                        {nonBundleParentMessage}
                      </CartAvailabilityError>
                    )}
                </>
            )}
            {pathname === `/${kitchenSlug}/checkout` && !isBundleSubItem && <CheckoutItemAction>
                {pathname === `/${kitchenSlug}/checkout` && (
                    <QuantityContainer>
                        {showQuantity ?
                            <Quantity decrement={removeItemHandler} increment={addItemHandler} isRemoveDisabled={itemQuantity === 1} label={itemQuantity} size='small' mode='action' />
                            :
                            <QuantityInfo onClick={() => setShowQuantity(!showQuantity)} backgroundTertiary={backgroundTertiary} brandTertiary={brandTertiary}>
                                {itemQuantity}x
                            </QuantityInfo>
                        }
                    </QuantityContainer>
                )}
                {pathname === `/${kitchenSlug}/checkout` && (
                    <ButtonAction
                        onClick={clearItemHandler}
                        label='Remove'
                        backgroundTertiary={backgroundTertiary}
                        brandPrimary={brandTertiary}
                    />

                )}
            </CheckoutItemAction>
            }
        </CheckoutItemContainer>
    )
}

export default CheckoutItem;
